import React, { useState, useEffect } from "react";
import { fetchPanelLogs, fetchPanelActions, fetchAdminUsers } from "../api/client";
import {
  History, Search, Filter, ChevronLeft, ChevronRight, RefreshCw,
  Calendar, User, Sliders, AlertTriangle
} from "lucide-react";
import { format } from "date-fns";

// Safe date formatter
const safeFormatDate = (dateStr) => {
  try {
    if (!dateStr) return "—";
    const dateObj = new Date(dateStr);
    if (isNaN(dateObj.getTime())) return "—";
    return format(dateObj, "dd MMM yyyy HH:mm:ss");
  } catch {
    return "—";
  }
};

// Render color badges based on action type
const getActionBadgeClass = (action) => {
  const a = action.toLowerCase();
  if (a.includes("login")) return "badge-blue";
  if (a.includes("logout")) return "badge-gray";
  if (a.includes("signup_completed") || a.includes("verified")) return "badge-green";
  if (a.includes("disconnected") || a.includes("deleted") || a.includes("rejected")) return "badge-red";
  if (a.includes("connected") || a.includes("created") || a.includes("approved")) return "badge-green";
  if (a.includes("webhook")) return "badge-cyan";
  if (a.includes("license")) return "badge-purple";
  return "badge-yellow";
};

export default function PanelLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionsList, setActionsList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);

  // Filters State
  const [filters, setFilters] = useState({
    action: "",
    user_id: "",
    date_from: "",
    date_to: ""
  });

  const [page, setPage] = useState(1);
  const limit = 20;

  // Load Actions and Users lists
  useEffect(() => {
    // Actions categories list
    fetchPanelActions()
      .then(data => setActionsList(data.actions || []))
      .catch(console.error);

    // Users list (Admin only)
    fetchAdminUsers(0, 100)
      .then(data => {
        setIsAdmin(true);
        setUsersList(data.users || []);
      })
      .catch(() => {
        setIsAdmin(false);
      });
  }, []);

  const loadLogs = (currentPage = page) => {
    setLoading(true);
    const skip = (currentPage - 1) * limit;
    fetchPanelLogs(skip, limit, filters)
      .then(data => {
        setLogs(data.logs || []);
        setTotal(data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadLogs(page);
  }, [page]);

  const handleApplyFilters = () => {
    setPage(1);
    loadLogs(1);
  };

  const handleResetFilters = () => {
    const cleanFilters = { action: "", user_id: "", date_from: "", date_to: "" };
    setFilters(cleanFilters);
    setPage(1);
    setLoading(true);
    fetchPanelLogs(0, limit, cleanFilters)
      .then(data => {
        setLogs(data.logs || []);
        setTotal(data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const totalPages = Math.ceil(total / limit) || 1;
  const startEntry = (page - 1) * limit + 1;
  const endEntry = Math.min(page * limit, total);

  return (
    <div style={{ padding: "24px", color: "var(--text)" }}>
      {/* Title Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
        <div>
          <h1 style={{ fontSize: "28px", fontWeight: "800", display: "flex", alignItems: "center", gap: "12px", margin: 0 }}>
            <History size={28} className="accent" />
            {isAdmin ? "System Activity Logs" : "Account Activity Logs"}
          </h1>
          <p style={{ color: "var(--text2)", fontSize: "14px", marginTop: "4px", marginBottom: 0 }}>
            {isAdmin 
              ? "Track, search, and audit system-wide user actions and API interactions." 
              : "Review all chronological events and connection audits for your account."}
          </p>
        </div>
        <button className="btn btn-ghost" onClick={() => loadLogs(page)} disabled={loading} style={{ gap: "8px" }}>
          <RefreshCw size={16} className={loading ? "spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Glassmorphic Filters Panel */}
      <div className="card" style={{ padding: "20px", marginBottom: "24px", backdropFilter: "blur(12px)", border: "1px solid var(--border)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px", borderBottom: "1px solid var(--border)", paddingBottom: "12px" }}>
          <Sliders size={18} className="accent" />
          <span style={{ fontWeight: "700", fontSize: "15px" }}>Advanced Filter Settings</span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
          {/* Action Type Dropdown */}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Action Event Type</label>
            <div style={{ position: "relative" }}>
              <select
                className="form-input"
                style={{ paddingRight: "30px", appearance: "none" }}
                value={filters.action}
                onChange={e => setFilters(p => ({ ...p, action: e.target.value }))}
              >
                <option value="">All Actions</option>
                <option value="login">Login</option>
                <option value="logout">Logout</option>
                <option value="new_account">New Account</option>
                <option value="add_account">Add Account</option>
                <option value="disconnected">Disconnected</option>
              </select>
              <Filter size={14} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", color: "var(--text2)", pointerEvents: "none" }} />
            </div>
          </div>

          {/* User Specific Dropdown (Admin only) */}
          {isAdmin && (
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Filter by User</label>
              <div style={{ position: "relative" }}>
                <select
                  className="form-input"
                  style={{ paddingRight: "30px", appearance: "none" }}
                  value={filters.user_id}
                  onChange={e => setFilters(p => ({ ...p, user_id: e.target.value }))}
                >
                  <option value="">All Users</option>
                  {usersList.map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                  ))}
                </select>
                <User size={14} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", color: "var(--text2)", pointerEvents: "none" }} />
              </div>
            </div>
          )}

          {/* Date From */}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Date From</label>
            <div style={{ position: "relative" }}>
              <input
                type="date"
                className="form-input"
                value={filters.date_from}
                onChange={e => setFilters(p => ({ ...p, date_from: e.target.value }))}
              />
            </div>
          </div>

          {/* Date To */}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Date To</label>
            <div style={{ position: "relative" }}>
              <input
                type="date"
                className="form-input"
                value={filters.date_to}
                onChange={e => setFilters(p => ({ ...p, date_to: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* Filter Action Buttons */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "20px" }}>
          <button className="btn btn-ghost" onClick={handleResetFilters}>
            Reset Filters
          </button>
          <button className="btn btn-primary" onClick={handleApplyFilters} style={{ gap: "8px" }}>
            <Search size={16} />
            Apply Search
          </button>
        </div>
      </div>

      {/* Logs Table Card */}
      <div className="card" style={{ padding: "0px", overflow: "hidden", border: "1px solid var(--border)" }}>
        {loading ? (
          <div style={{ padding: "80px", textAlign: "center", color: "var(--text2)" }}>
            <RefreshCw size={36} className="spin accent" style={{ marginBottom: "16px" }} />
            <div>Fetching activity records...</div>
          </div>
        ) : logs.length === 0 ? (
          <div style={{ padding: "80px", textAlign: "center", color: "var(--text2)" }}>
            <AlertTriangle size={36} style={{ color: "var(--yellow)", marginBottom: "16px" }} />
            <div style={{ fontWeight: "700", color: "var(--text)" }}>No Log Entries Found</div>
            <div style={{ fontSize: "13px", marginTop: "4px" }}>Try relaxing your filter parameters or checking another timeframe.</div>
          </div>
        ) : (
          <>
            <div style={{ overflowX: "auto" }}>
              <table className="table" style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--border)", background: "rgba(255,255,255,0.02)" }}>
                    <th style={{ padding: "14px 18px", textAlign: "left", fontSize: "13px", fontWeight: "700", color: "var(--text2)" }}>Timestamp</th>
                    <th style={{ padding: "14px 18px", textAlign: "left", fontSize: "13px", fontWeight: "700", color: "var(--text2)" }}>Action Category</th>
                    {isAdmin && (
                      <th style={{ padding: "14px 18px", textAlign: "left", fontSize: "13px", fontWeight: "700", color: "var(--text2)" }}>User Details</th>
                    )}
                    <th style={{ padding: "14px 18px", textAlign: "left", fontSize: "13px", fontWeight: "700", color: "var(--text2)" }}>Activity Description</th>
                    <th style={{ padding: "14px 18px", textAlign: "left", fontSize: "13px", fontWeight: "700", color: "var(--text2)" }}>IP Address</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, idx) => (
                    <tr key={log.id} style={{ borderBottom: "1px solid var(--border)", transition: "background 0.15s" }} className="table-row-hover">
                      <td style={{ padding: "14px 18px", fontSize: "13px", color: "var(--text2)", whiteSpace: "nowrap" }}>
                        {safeFormatDate(log.timestamp)}
                      </td>
                      <td style={{ padding: "14px 18px" }}>
                        <span className={`badge ${getActionBadgeClass(log.action)}`}>
                          {log.action.replace(/_/g, " ")}
                        </span>
                      </td>
                      {isAdmin && (
                        <td style={{ padding: "14px 18px", fontSize: "13px" }}>
                          <div style={{ fontWeight: "600" }}>{log.user_name}</div>
                          <div style={{ fontSize: "11px", color: "var(--text2)" }}>{log.user_email}</div>
                        </td>
                      )}
                      <td style={{ padding: "14px 18px", fontSize: "13px", color: "var(--text)", lineHeight: "1.4" }}>
                        {log.details}
                      </td>
                      <td style={{ padding: "14px 18px", fontSize: "13px", color: "var(--text2)" }}>
                        {log.ip_address || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderTop: "1px solid var(--border)", background: "rgba(255,255,255,0.01)" }}>
              <div style={{ fontSize: "13px", color: "var(--text2)" }}>
                Showing <strong style={{ color: "var(--text)" }}>{startEntry}</strong> to{" "}
                <strong style={{ color: "var(--text)" }}>{endEntry}</strong> of{" "}
                <strong style={{ color: "var(--text)" }}>{total}</strong> journal entries
              </div>

              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  className="btn btn-ghost"
                  style={{ padding: "8px 12px", minHeight: "auto" }}
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  <ChevronLeft size={16} />
                  Prev
                </button>
                <button
                  className="btn btn-ghost"
                  style={{ padding: "8px 12px", minHeight: "auto" }}
                  disabled={page === totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
